/**
 * Un contatto condiviso in chat (vCard): "Scrivi" apre la chat con quel numero,
 * "Crea Lead" apre il modulo del nuovo Lead gia' compilato (nome, numero, fonte
 * WhatsApp) senza uscire dalla chat. Se il numero e' gia' un Lead/Cliente visibile
 * all'utente, al posto di "Crea Lead" c'e' "Apri" (niente doppioni).
 * La ricerca per numero e' quella della nuova chat (searchClientsForChat), che
 * rispetta la visibilita' del ruolo.
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { User, MessageCircle, UserPlus, ExternalLink } from 'lucide-react';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { useWhatsAppDrawerStore } from '@/lib/stores/whatsappDrawerStore';
import { useAuth } from '@/lib/auth';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { normalizeWhatsAppDigits, etichettaNumero } from '@/lib/services/whatsapp/phoneDigits';
import { searchClientsForChat } from '@/lib/services/whatsapp/clientPhoneSearch';
import { findCandidateByNumber } from '@/lib/utils/chatLeadPrefill';
import { CreateClientModal } from '@/components/clients/CreateClientModal';
import { tip } from '@/components/messages/ChatTooltip';
import { useChatLeadPrefill } from '../shared/useChatLeadPrefill';
import type { ContactCard } from './messageExtras';

type Existing = { id: string; kind: 'Lead' | 'Cliente' };

export function ContactCardRow({ card, mutedClass }: { card: ContactCard; mutedClass: string }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canCreateLead } = usePermissions();
  const wahaConfig = useWhatsAppChatStore((s) => s.wahaConfig);
  const prefillLead = useChatLeadPrefill();
  const [existing, setExisting] = useState<Existing | null | undefined>(undefined);
  const [createData, setCreateData] = useState<Record<string, string> | null>(null);
  const digits = normalizeWhatsAppDigits(card.phone);
  const orgId = wahaConfig?.organizationId;

  useEffect(() => {
    if (!digits || !orgId) { setExisting(null); return; }
    let cancelled = false;
    searchClientsForChat(orgId, user?.id ?? null, digits, 5)
      .then((rows) => {
        const hit = findCandidateByNumber(rows, digits);
        if (!cancelled) setExisting(hit ? { id: hit.id, kind: hit.kind } : null);
      })
      .catch(() => { if (!cancelled) setExisting(null); });
    return () => { cancelled = true; };
  }, [digits, orgId, user?.id]);

  const openChat = () => {
    if (!digits) return;
    useWhatsAppDrawerStore.getState().openWithChat(digits, wahaConfig?.configId ?? null, card.name);
  };

  const openExisting = () => {
    if (!existing) return;
    navigate(existing.kind === 'Lead' ? `/leads/${existing.id}` : `/clients/${existing.id}`);
  };

  const openCreate = async () => {
    setCreateData(await prefillLead({
      channel: 'whatsapp',
      name: card.name,
      phone: digits,
      receivedOn: etichettaNumero(wahaConfig?.phoneNumber),
    }));
  };

  const btn = 'flex items-center gap-1 px-2 py-1 rounded-md text-xs';

  return (
    <div className="flex items-center gap-2 py-1 min-w-[220px]">
      <div className="w-9 h-9 rounded-full bg-[rgba(55,53,47,0.06)] dark:bg-white/10 flex items-center justify-center flex-shrink-0">
        <User className="w-5 h-5 text-[#787774]" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{card.name}</p>
        {card.phone && <p className={`text-xs truncate ${mutedClass}`}>{card.phone}</p>}
      </div>
      {digits && (
        <button type="button" onClick={(e) => { e.stopPropagation(); openChat(); }}
          className={`${btn} text-[#2383E2] hover:bg-[#2383E2]/10`}
          {...tip('Apri una chat WhatsApp con questo numero')}>
          <MessageCircle className="w-3.5 h-3.5" /> Scrivi
        </button>
      )}
      {existing && (
        <button type="button" onClick={(e) => { e.stopPropagation(); openExisting(); }}
          className={`${btn} text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10`}
          {...tip(`Questo numero e' gia' un ${existing.kind}: apri la scheda (chiude i messaggi)`)}>
          <ExternalLink className="w-3.5 h-3.5" /> Apri
        </button>
      )}
      {existing === null && canCreateLead && (
        <button type="button" onClick={(e) => { e.stopPropagation(); void openCreate(); }}
          className={`${btn} text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10`}
          {...tip('Crea un nuovo Lead con nome, numero e fonte WhatsApp gia\' compilati')}>
          <UserPlus className="w-3.5 h-3.5" /> Crea Lead
        </button>
      )}
      {/* Portal e propagazione fermata: il modulo non deve far scattare i clic della bolla. */}
      {createData && createPortal(
        <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} onContextMenu={(e) => e.stopPropagation()}>
          <CreateClientModal
            isOpen
            onClose={() => setCreateData(null)}
            initialName={card.name}
            initialData={createData}
            isLead
            onClientCreated={(id) => { setCreateData(null); setExisting({ id, kind: 'Lead' }); }}
          />
        </div>,
        document.body,
      )}
    </div>
  );
}
